import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram } from "lucide-react";

const navLinks = [
  { label: "Studios", href: "#studios" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Team", href: "#team" },
  { label: "Preise", href: "#preise" },
  { label: "Kontakt", href: "#kontakt" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <header
        data-testid="header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-[#5A4A1A]/40 py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <a href="#hero" className="flex items-center gap-3 group">
            <svg viewBox="0 0 40 28" className="w-8 h-6" fill="none">
              <path
                d="M2,26 L2,18 L9,5 L16,14 L20,2 L24,14 L31,5 L38,18 L38,26 Z"
                stroke="#C9A84C"
                strokeWidth="1.5"
                strokeLinejoin="round"
                fill="none"
              />
              <line x1="2" y1="26" x2="38" y2="26" stroke="#C9A84C" strokeWidth="1.5" />
              <circle cx="20" cy="2" r="1.5" fill="#C9A84C" />
              <circle cx="9" cy="5" r="1.2" fill="#C9A84C" />
              <circle cx="31" cy="5" r="1.2" fill="#C9A84C" />
            </svg>
            <div>
              <span className="font-cormorant text-lg tracking-[0.2em] text-[#F0F0F0] group-hover:text-[#C9A84C] transition-colors">
                ROYAL TATTOO
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className="text-xs text-[#888888] hover:text-[#C9A84C] tracking-[0.15em] uppercase font-outfit transition-colors duration-300"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#kontakt"
              data-testid="header-cta"
              className="gold-btn text-xs py-2 px-6"
            >
              Termin anfragen
            </a>
          </nav>

          {/* Mobile Hamburger */}
          <button
            data-testid="hamburger-menu"
            onClick={() => setMenuOpen(true)}
            className="lg:hidden text-[#C9A84C] p-2"
            aria-label="Menü öffnen"
          >
            <Menu size={24} />
          </button>
        </div>
      </header>

      {/* Mobile Full-Screen Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[60] bg-[#0A0A0A] flex flex-col items-center justify-center"
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
          >
            <button
              onClick={closeMenu}
              className="absolute top-6 right-6 text-[#888888] hover:text-[#C9A84C] transition-colors"
              aria-label="Menü schließen"
            >
              <X size={28} />
            </button>

            <nav className="flex flex-col items-center gap-8">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={closeMenu}
                  className="font-cormorant text-4xl italic text-[#F0F0F0] hover:text-[#C9A84C] transition-colors tracking-wide"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                >
                  {link.label}
                </motion.a>
              ))}
              <motion.a
                href="#kontakt"
                onClick={closeMenu}
                className="gold-btn mt-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                Termin anfragen
              </motion.a>
            </nav>

            <div className="absolute bottom-10 flex gap-6">
              <a href="https://instagram.com/royal.tattoo.grevenbroich" target="_blank" rel="noopener noreferrer" className="text-[#888888] hover:text-[#C9A84C] transition-colors">
                <Instagram size={20} />
              </a>
              <a href="https://instagram.com/royal.tattoo.leverkusen" target="_blank" rel="noopener noreferrer" className="text-[#888888] hover:text-[#C9A84C] transition-colors">
                <Instagram size={20} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
